import { BASE_TABLE_PROPS } from "@/Washouts/constants";
// Redux
import { useSelector } from "react-redux";
// MUI
import { Box, Grid, Skeleton, Typography } from "@mui/material"; // MUI
import { GridFilterModel } from "@mui/x-data-grid-premium";

// REACT
import { Dispatch, SetStateAction } from "react";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// Queries
import { getFniProductsByPayPeriod } from "@/DataPages/api/fniProducts";
import { useQuery } from "@tanstack/react-query";

// Components
import CancellationTable from "@Washouts/components/tables/Finance/Managers/CancellationTable/CancellationTable.tsx";

// Services
import actions from "@Constants/actionsList";

// Types
import { RootState } from "@/redux/store";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";

import { and } from "@/Common/helpers/sonar";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

type FniProductsBodyProps = {
	selected: IPeriodTreeSelection;
	isTabSelected: boolean;
	filterModel?: GridFilterModel;
	setFilterModel?: Dispatch<SetStateAction<GridFilterModel>>;
	hasPermission: boolean;
};

export default function FniProductsBody(props: Readonly<FniProductsBodyProps>) {
	const { selected, isTabSelected, filterModel, setFilterModel, hasPermission } = props;
	const { useAuth } = useAuthorization();
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;
	const payPeriodId = selected?.payPeriodId;
	const commissionCycleId = selected?.cycleId;
	const hasViewGPSEmbeddedURLPermission = useAuth(actions.VIEW_CIT_REPORTS);

	/********************************************** QUERIES **********************************************/
	const { data, isLoading } = useQuery({
		queryKey: ["fniProductMgmt", locationId, commissionCycleId, payPeriodId],
		queryFn: async () => getFniProductsByPayPeriod({ locationId, commissionCycleId, payPeriodId }),
		staleTime: Infinity,
		enabled: and(!!locationId, !!payPeriodId, !!isTabSelected, hasPermission),
	});
	const selectedTitle = selected?.titleExtended?.split(" ").slice(0, -1).join(" ");
	if (!hasViewGPSEmbeddedURLPermission) {
		return (
			<EmptyBody
				image={empty}
				title={"You do not have access to Cancellations"}
				bodyHeight={"480px"}
				sx={{
					boxShadow: "none !important",
				}}
			/>
		);
	}
	return (
		<Box className="py-8" height={690}>
			<Grid>
				{!selected ? (
					<Skeleton variant="rectangular" height={30} width={350} className={"rounded-[6px] mb-[15px]"} />
				) : (
					selected?.title && <Typography className={"page-title !mb-[-22px]"}>{`${selectedTitle} Cancellations`}</Typography>
				)}
			</Grid>
			{!isLoading && !data?.length && (
				<EmptyBody
					image={selected ? emptyTable : empty}
					title={"No Cancellations Data for this month"}
					bodyHeight={"480px"}
					sx={{
						boxShadow: "none !important",
					}}
				/>
			)}
			<Grid item xs={12}>
				{isLoading ? (
					<TableLoader toolbar buttons={2} content rows={5} multiTitles={8} />
				) : (
					!!data?.length && (
						<CancellationTable
							title={"Cancellations"}
							divider={false}
							data={data}
							dataPresent={true}
							tableProps={{ ...BASE_TABLE_PROPS }}
							notCollapsible={true}
							loading={isLoading}
							includeEmployeeNameColumn={true}
							filterModel={filterModel}
							setFilterModel={setFilterModel}
						/>
					)
				)}
			</Grid>
		</Box>
	);
}
